class FGPopupBanner extends HTMLElement {
    static get observedAttributes() {
        return [
            'form-selector',
            'success-selector',
            'redirect-url',
            'redirect-delay-ms',
            'auto-open',
            'auto-open-delay-ms',
            'auto-open-scope'
        ];
    }

    connectedCallback() {
        this._content = this.querySelector('[data-ref="content"]');
        this._closeBtn = this.querySelectorAll('[data-ref="close"]');

        this._closeBtn.forEach((btn) => {
            btn && btn.addEventListener('click', () => this.close(), {passive: true});
        })

        this.addEventListener('click', (e) => {
            if (e.target === this) this.close();
        }, {passive: true});
        this._onKeydown = (e) => {
            if (e.key === 'Escape') this.close();
        };
        document.addEventListener('keydown', this._onKeydown);

        this._readAttrs();
        this.__openedOnce = false;
        this._observer = null;

        this._bindTargets();
        this._maybeAutoOpen();
    }

    attributeChangedCallback() {
        this._disconnectObserver();
        this._readAttrs();
        this._bindTargets();
        this._maybeAutoOpen();
    }

    disconnectedCallback() {
        document.removeEventListener('keydown', this._onKeydown);
        this._disconnectObserver();
    }

    open() {
        if (this.__openedOnce) return;
        this.__openedOnce = true;
        this.classList.remove('tw-hidden');
        this.removeAttribute('aria-hidden');
        queueMicrotask(() => {
            this._closeBtn && this._closeBtn.focus();
        });
    }

    close() {
        this.classList.add('tw-hidden');
        this.setAttribute('aria-hidden', 'true');
    }

    // ---------- internals ----------
    _readAttrs() {
        this._formSelector = this.getAttribute('form-selector') || '#contact_form';
        this._successSelector = this.getAttribute('success-selector') || '.newsletter-form__message--success';
        this._redirectUrl = this.getAttribute('redirect-url') || '';
        this._redirectDelayMs = parseInt(this.getAttribute('redirect-delay-ms') || '600', 10);

        this._autoOpen = (this.getAttribute('auto-open') || 'false') === 'true';
        this._autoOpenDelayMs = parseInt(this.getAttribute('auto-open-delay-ms') || '600', 10);
        this._autoOpenScope = this.getAttribute('auto-open-scope') || 'session'; // 'session' | 'always'
    }

    _bindTargets() {
        this._form = document.querySelector(this._formSelector);
        if (!this._form) return;

        this._successEl = this._form.querySelector(this._successSelector);

        // Se già visibile al mount
        if (this._isVisible(this._successEl)) {
            this._onSuccessDetected();
            return;
        }

        // Osserva il toggle della classe/style sul nodo di successo
        if (this._successEl) {
            this._observer = new MutationObserver(() => {
                if (this._isVisible(this._successEl)) {
                    this._onSuccessDetected();
                    this._disconnectObserver();
                }
            });
            this._observer.observe(this._successEl, {attributes: true, attributeFilter: ['class', 'style']});
        }

        // Evento custom opzionale
        window.addEventListener('newsletter:success', () => {
            this._onSuccessDetected();
        }, {once: true});

        // Se il nodo di successo viene (ri)inserito dopo
        const formObserver = new MutationObserver(() => {
            const maybe = this._form.querySelector(this._successSelector);
            if (maybe && maybe !== this._successEl) {
                this._successEl = maybe;
                if (this._isVisible(this._successEl)) {
                    this._onSuccessDetected();
                    formObserver.disconnect();
                } else {
                    this._observer && this._observer.disconnect();
                    this._observer = new MutationObserver(() => {
                        if (this._isVisible(this._successEl)) {
                            this._onSuccessDetected();
                            this._disconnectObserver();
                            formObserver.disconnect();
                        }
                    });
                    this._observer.observe(this._successEl, {attributes: true, attributeFilter: ['class', 'style']});
                }
            }
        });
        formObserver.observe(this._form, {childList: true, subtree: true});

        const url = new URL(window.location.href);
        const hashHasContactForm = (url.hash || '').toLowerCase().includes('contact_form');

        const customerPostedTrue = url.searchParams.get('customer_posted') === 'true';
        const contactPosted = url.searchParams.has('contact_posted');
        const formTypeCustomer = (url.searchParams.get('form_type') || '').toLowerCase() === 'customer';

        const urlHintsSuccess =
            hashHasContactForm && (customerPostedTrue || contactPosted || formTypeCustomer);

        if (urlHintsSuccess) {
            queueMicrotask(() => this._onSuccessDetected());
        }
    }

    _maybeAutoOpen() {
        if (!this._autoOpen) return;

        const isInternalForm = this.contains(this._form);
        if (!isInternalForm) return;

        if (this._autoOpenScope === 'session') {
            const key = this._seenKey();
            if (this._getCookie(key) === '1') return;
            this._setCookie(key, '1', 30);
        }

        setTimeout(() => this.open(), Math.max(0, this._autoOpenDelayMs || 0));
    }

    _onSuccessDetected() {
        this.open();

        const isInternalForm = this.contains(this._form);
        if (isInternalForm && this._redirectUrl) {
            this.close();
            setTimeout(() => {
                window.location.assign(this._redirectUrl);
            }, Math.max(0, this._redirectDelayMs || 0));
        }
    }

    _disconnectObserver() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
    }

    _isVisible(el) {
        if (!el) return false;
        const style = getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity || '1') === 0) return false;
        return el.offsetParent !== null || style.position === 'fixed';
    }

    _seenKey() {
        // key unica per componente (usata nel cookie)
        const id = this.getAttribute('id') || 'fg-popup';
        return `fg_pb_seen__${id}`;
    }

    // ---------- cookie helpers (globali) ----------
    _setCookie(name, value, days) {
        try {
            const d = new Date();
            d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
            const expires = `expires=${d.toUTCString()}`;
            // Path globale, SameSite=Lax. Domain omesso per ereditare l’host corrente.
            document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=Lax`;
        } catch (_) {
            // no-op
        }
    }

    _getCookie(name) {
        try {
            const n = encodeURIComponent(name) + '=';
            const parts = document.cookie.split(';');
            for (let c of parts) {
                while (c.charAt(0) === ' ') c = c.substring(1);
                if (c.indexOf(n) === 0) return decodeURIComponent(c.substring(n.length));
            }
        } catch (_) {
            // ignore
        }
        return null;
    }
}

if (!customElements.get('fg-popup-banner')) {
    customElements.define('fg-popup-banner', FGPopupBanner);
}
